import createCollectionResource from 'src/actions/shared/createResource';
import * as sharedTypes from 'src/actions/shared/sharedTypes';
import { DomainError } from 'src/models/DomainError';
import validateType from 'src/adapters/validateType';
import PhoneNumber from 'src/models/PhoneNumberDef';
import CompanyPhoneNumber from 'src/models/CompanyPhoneNumber/CompanyPhoneNumber';
import emptyObserverCreator from 'src/actions/shared/emptyObserverCreator';
import { WhatsAppSender } from 'src/models/Channels/WhatsAppSender';
import { NotExistError } from '@ad-scripts/application/lib/error';

const whatsAppSenderResource = createCollectionResource<WhatsAppSender>({
  name: 'whatsAppSender',
  endpoint: 'v1/orgs/:orgId/whatsapp/senders/:sid',
  type: WhatsAppSender,
  observerCreator: emptyObserverCreator,
  options: {
    toastMessages: {
      onAdd: sender => {
        return `${sender.phoneNumber} is connected to a WhatsApp Business Account`;
      },
      onAddFail: (sender, err) => getErrorToastMessage(sender, err),
      onRemove: sender => {
        return `${sender.phoneNumber} is no longer connected to a WhatsApp Business Account in Gladly.`;
      },
      onRemoveFail: sender => {
        return `Failed to disconnect ${sender.phoneNumber} from a WhatsApp Business Account in Gladly. Please try again.`;
      },
    },
  },
});

export type WhatsAppConfigurationFields = {
  displayName: string;
  isGladlyNumber: boolean;
  ownPhoneNumber: string;
  gladlyPhoneNumber: string;
  phoneSid: string;
  availableNumbers: CompanyPhoneNumber[];
  usedNumbers: string[];
};

const validateNonGladlyPhoneNumber: sharedTypes.AppThunk = (fields: WhatsAppConfigurationFields) => dispatch => {
  if (fields.isGladlyNumber) {
    return true;
  }

  const errs: DomainError[] = [];
  try {
    const phoneNumber = fields.ownPhoneNumber;
    validateType(PhoneNumber, { phoneNumber });
  } catch (err) {
    errs.push({
      attr: 'ownPhoneNumber',
      code: 'invalid',
      detail: 'The phone number is not valid',
    });
  }
  if (fields.availableNumbers.find(number => number.phoneNumber === fields.ownPhoneNumber)) {
    errs.push({
      attr: 'ownPhoneNumber',
      code: 'invalid',
      detail:
        'You already own this number. Select this number from the "Connect a Gladly SMS-capable phone number" option.',
    });
  }
  if (fields.usedNumbers.find(number => number === fields.ownPhoneNumber)) {
    errs.push({
      attr: 'ownPhoneNumber',
      code: 'in_use',
      detail: 'This number is already used for WhatsApp',
    });
  }

  if (errs.length > 0) {
    dispatch(whatsAppSenderResource.actions.validationError(errs));
    return false;
  }

  return true;
};

whatsAppSenderResource.effects.validateNonGladlyPhoneNumber = validateNonGladlyPhoneNumber;

const getErrorToastMessage = (sender: WhatsAppSender, err: any) => {
  return err instanceof NotExistError
    ? `Failed to connect ${sender.phoneNumber} to a WhatsApp Business Account. Please contact Gladly Support.`
    : `Failed to connect ${sender.phoneNumber} to a WhatsApp Business Account. Please try again.`;
};

export default whatsAppSenderResource;
